/**
 * Free Cushion Pad Offer - Interactive Script
 * Handles variant detection, size matching, and cart addition
 */

class FreeCushionPadOffer {
  constructor(container) {
    this.container = container;
    this.button = container.querySelector('[data-add-bundle-button]');
    this.sizeDisplay = container.querySelector('[data-size-display]');
    this.rugSizeEl = container.querySelector('[data-rug-size]');
    this.padSizeEl = container.querySelector('[data-pad-size]');
    this.messageEl = container.querySelector('[data-message]');
    this.tooltipTrigger = container.querySelector('.free-cushion-pad-offer__tooltip-trigger');
    this.tooltipContent = container.querySelector('.free-cushion-pad-offer__tooltip-content');
    
    // Sticky mobile elements
    this.stickyBar = document.querySelector('[data-sticky-bundle-button]');
    this.stickyButton = this.stickyBar?.querySelector('[data-sticky-add-button]');
    this.stickyText = this.stickyBar?.querySelector('[data-sticky-size-text]');
    
    console.log('🔍 Sticky elements check:', {
      stickyBar: !!this.stickyBar,
      stickyButton: !!this.stickyButton,
      stickyText: !!this.stickyText,
      buttonElement: this.stickyButton
    });
    
    if (this.stickyButton) {
      console.log('  Sticky button initial state:', {
        disabled: this.stickyButton.disabled,
        classList: this.stickyButton.className
      });
    }
    
    // Parse JSON data
    this.cushionData = this.parseJSON('[data-cushion-variants]');
    this.rugData = this.parseJSON('[data-rug-variants]');
    
    // State
    this.currentRugVariantId = null;
    this.currentPadVariantId = null;
    this.isLoading = false;
    
    this.init();
  }

  parseJSON(selector) {
    const script = this.container.querySelector(selector);
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      console.error('Failed to parse JSON:', selector, e);
      return null;
    }
  }

  init() {
    // Parse the size-to-pad mapping
    this.sizeToPadMapping = this.parseJSON('[data-size-to-pad-mapping]');
    
    // Store reference to original add to cart button
    this.originalAddButton = null;
    this.findOriginalButton();
    
    // Bind events
    if (this.button) {
      this.button.addEventListener('click', () => this.handleAddToCart());
    }
    
    // Bind sticky button
    if (this.stickyButton) {
      this.stickyButton.addEventListener('click', () => this.handleAddToCart());
    }
    
    if (this.tooltipTrigger) {
      this.tooltipTrigger.addEventListener('click', () => this.toggleTooltip());
    }

    // Close tooltip on outside click
    document.addEventListener('click', (e) => {
      if (this.tooltipTrigger && !this.tooltipTrigger.contains(e.target) && 
          this.tooltipContent && !this.tooltipContent.contains(e.target)) {
        this.closeTooltip();
      }
    });

    // Setup sticky button scroll detection
    this.setupStickyButton();

    // Listen for variant changes on the page
    this.observeVariantChanges();
    
    // Initial detection
    this.detectCurrentVariant();
  }

  /**
   * Setup sticky button to show/hide on scroll
   */
  setupStickyButton() {
    if (!this.stickyBar) {
      console.warn('⚠️ Sticky bar not found for scroll detection');
      return;
    }
    
    let ticking = false;
    
    const updateSticky = () => {
      const scrollY = window.scrollY;
      const containerRect = this.container.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const isMobile = window.innerWidth < 990;
      
      // Show sticky when:
      // 1. Scrolled past the main section OR scrolled down at least 300px
      // 2. On mobile (< 990px)
      // 3. Have a matching pad available
      
      const isPastSection = containerRect.bottom < 0 || scrollY > 300;
      const hasMatch = this.currentPadVariantId !== null;
      
      const shouldShow = isPastSection && isMobile && hasMatch;
      
      if (shouldShow) {
        this.stickyBar.classList.add('visible');
        
        // Force re-enable button when showing (in case it got disabled)
        if (this.stickyButton && this.currentPadVariantId) {
          this.stickyButton.disabled = false;
          this.stickyButton.removeAttribute('disabled');
          console.log('✓ Sticky visible & enabled (scrollY:', scrollY, 'disabled attr:', this.stickyButton.hasAttribute('disabled'), ')');
        }
      } else {
        this.stickyBar.classList.remove('visible');
      }
      
      ticking = false;
    };
    
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateSticky);
        ticking = true;
      }
    };
    
    // Initial check
    updateSticky();
    
    // Listen for scroll and resize
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateSticky, { passive: true });
    
    console.log('✓ Sticky scroll detection setup complete');
  }

  /**
   * Find and store reference to the original add to cart button
   * We'll intercept clicks instead of hiding it
   */
  findOriginalButton() {
    // Try multiple approaches to find the add to cart button
    
    // Method 1: Find by form and button
    let productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      this.originalAddButton = productForm.querySelector('[name="add"]') ||
                               productForm.querySelector('[data-add-to-cart]') ||
                               productForm.querySelector('.product-form__submit button') ||
                               productForm.querySelector('button[type="submit"]');
    }
    
    // Method 2: Direct selectors if form method failed
    if (!this.originalAddButton) {
      this.originalAddButton = document.querySelector('.product-form__submit button[name="add"]') ||
                               document.querySelector('[data-add-to-cart]') ||
                               document.querySelector('.product-form button[name="add"]');
    }
    
    if (this.originalAddButton) {
      console.log('✓ Found original button - will intercept clicks');
      this.interceptOriginalButton();
    } else {
      console.warn('⚠️ Could not find original add to cart button');
    }
    
    // Find theme's sticky/mobile button (the --fixed one)
    this.themeStickyButton = document.querySelector('.product-form__cart-submit--fixed');
    
    if (this.themeStickyButton) {
      console.log('✓ Found theme sticky button - will intercept clicks');
      this.interceptThemeStickyButton();
    }
  }

  /**
   * Intercept original button clicks to add cushion pad
   */
  interceptOriginalButton() {
    if (!this.originalAddButton) return;
    
    this.originalAddButton.addEventListener('click', async (e) => {
      // Only intercept if we have a matching cushion pad
      if (this.currentPadVariantId && this.currentRugVariantId) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        console.log('🎯 Intercepted add to cart - adding bundle!');
        await this.handleAddToCart();
      }
      // Otherwise let the original button work normally
    }, true); // Use capture phase to intercept early
  }

  /**
   * Intercept theme's sticky button clicks
   */
  interceptThemeStickyButton() {
    if (!this.themeStickyButton) return;
    
    this.themeStickyButton.addEventListener('click', async (e) => {
      // Only intercept if we have a matching cushion pad
      if (this.currentPadVariantId && this.currentRugVariantId) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        console.log('🎯 Intercepted sticky button - adding bundle!');
        await this.handleAddToCart();
      }
      // Otherwise let it work normally
    }, true); // Use capture phase
  }

  /**
   * Update button labels to show bundle offer
   */
  toggleOriginalButton(shouldIntercept) {
    if (shouldIntercept) {
      // Update button text to show it includes free pad
      const allAddButtons = document.querySelectorAll('[data-add-to-cart-text]');
      
      allAddButtons.forEach(textEl => {
        if (!textEl.hasAttribute('data-original-text')) {
          textEl.setAttribute('data-original-text', textEl.textContent);
          textEl.textContent = '+ Free Cushion Pad';
        }
      });
      
      console.log('✓ Updated button text to show free pad offer');
      
    } else {
      // Restore original button text
      const allAddButtons = document.querySelectorAll('[data-add-to-cart-text][data-original-text]');
      
      allAddButtons.forEach(textEl => {
        const originalText = textEl.getAttribute('data-original-text');
        if (originalText) {
          textEl.textContent = originalText;
          textEl.removeAttribute('data-original-text');
        }
      });
      
      console.log('✓ Restored original button text');
    }
  }

  /**
   * Observe variant changes from the product form
   * Multiple possible approaches for different themes
   */
  observeVariantChanges() {
    // Method 1: Listen to variant selector changes
    const variantSelectors = document.querySelectorAll('select[name="id"], input[name="id"]');
    variantSelectors.forEach(selector => {
      selector.addEventListener('change', () => {
        setTimeout(() => this.detectCurrentVariant(), 100);
      });
    });

    // Method 2: Listen to product form updates (Dawn theme standard)
    document.addEventListener('variant:change', (event) => {
      if (event.detail && event.detail.variant) {
        this.updateVariant(event.detail.variant.id);
      }
    });

    // Method 3: Observer for URL changes (if variant is in URL)
    if (window.history && window.history.pushState) {
      const originalPushState = window.history.pushState;
      window.history.pushState = (...args) => {
        originalPushState.apply(window.history, args);
        setTimeout(() => this.detectCurrentVariant(), 100);
      };
    }

    // Method 4: MutationObserver on price/option elements
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      const observer = new MutationObserver(() => {
        this.detectCurrentVariant();
      });
      observer.observe(productForm, { 
        childList: true, 
        subtree: true, 
        attributes: true,
        attributeFilter: ['data-variant-id', 'value']
      });
    }
  }

  /**
   * Detect the currently selected variant from the product form
   */
  detectCurrentVariant() {
    let variantId = null;

    // Try multiple detection methods
    
    // 1. Hidden input with name="id"
    const hiddenInput = document.querySelector('input[name="id"][type="hidden"]');
    if (hiddenInput && hiddenInput.value) {
      variantId = hiddenInput.value;
    }

    // 2. Selected radio button
    if (!variantId) {
      const radioInput = document.querySelector('input[name="id"]:checked');
      if (radioInput) {
        variantId = radioInput.value;
      }
    }

    // 3. Select dropdown
    if (!variantId) {
      const selectInput = document.querySelector('select[name="id"]');
      if (selectInput && selectInput.value) {
        variantId = selectInput.value;
      }
    }

    // 4. Data attribute on form
    if (!variantId) {
      const form = document.querySelector('form[action*="/cart/add"]');
      if (form && form.dataset.variantId) {
        variantId = form.dataset.variantId;
      }
    }

    // 5. URL parameter
    if (!variantId) {
      const urlParams = new URLSearchParams(window.location.search);
      variantId = urlParams.get('variant');
    }

    // 6. Check option selectors and match to variant
    if (!variantId && this.rugData && this.rugData.variants) {
      const selectedOptions = this.getSelectedOptions();
      if (selectedOptions.length > 0) {
        const matchedVariant = this.rugData.variants.find(v => {
          return selectedOptions.every((opt, idx) => {
            const optionKey = `option${idx + 1}`;
            return v[optionKey] === opt;
          });
        });
        if (matchedVariant) {
          variantId = matchedVariant.id;
        }
      }
    }

    if (variantId) {
      this.updateVariant(String(variantId));
    }
  }

  /**
   * Get currently selected option values from the product form
   */
  getSelectedOptions() {
    const options = [];
    
    // Look for option selectors (common pattern)
    const selectors = document.querySelectorAll('[name^="options["], select[id^="Option"], input[name^="option"]:checked');
    selectors.forEach(selector => {
      if (selector.value) {
        options.push(selector.value);
      }
    });

    return options;
  }

  /**
   * Update the current variant and find matching cushion pad
   */
  updateVariant(variantId) {
    if (this.currentRugVariantId === variantId) return;
    
    this.currentRugVariantId = variantId;
    
    // Find rug variant details
    const rugVariant = this.rugData.variants.find(v => String(v.id) === String(variantId));
    
    if (!rugVariant) {
      this.disableButton('Rug variant not found');
      this.toggleOriginalButton(false); // Show original button
      return;
    }
    
    // Get the size from the rug variant (typically option1)
    const rugSize = rugVariant.option1 || rugVariant.title;
    
    if (!rugSize) {
      this.disableButton('Size not found');
      this.toggleOriginalButton(false); // Show original button
      return;
    }
    
    // Find matching cushion pad variant ID based on size
    let padVariantId = this.sizeToPadMapping[rugSize];
    
    if (!padVariantId) {
      // Try to find a close match by cleaning up the size string
      const cleanSize = rugSize.trim();
      padVariantId = this.sizeToPadMapping[cleanSize];
      
      if (!padVariantId) {
        this.disableButton(`No matching cushion pad for size: ${rugSize}`);
        this.toggleOriginalButton(false); // Show original button (no match)
        console.log('Available sizes:', Object.keys(this.sizeToPadMapping));
        console.log('Looking for:', rugSize);
        return;
      }
    }
    
    // Find cushion variant details
    const padVariant = this.cushionData.variants.find(v => String(v.id) === String(padVariantId));
    
    if (!padVariant) {
      this.disableButton('Cushion pad variant not found');
      this.toggleOriginalButton(false); // Show original button
      return;
    }
    
    this.currentPadVariantId = padVariantId;
    
    // Update UI
    this.updateUI(rugVariant, padVariant);
    
    // Enable/disable button based on availability
    if (padVariant.available && rugVariant.available) {
      this.enableButton();
      this.toggleOriginalButton(true); // Hide original button (we have a match!)
    } else {
      this.disableButton('Currently unavailable');
      this.toggleOriginalButton(false); // Show original button if not available
    }
  }

  /**
   * Update the UI with variant information
   */
  updateUI(rugVariant, padVariant) {
    if (this.rugSizeEl && rugVariant) {
      // Extract size from variant title (first option is typically size)
      const rugSize = rugVariant.option1 || rugVariant.title || '—';
      this.rugSizeEl.textContent = rugSize;
    }
    
    if (this.padSizeEl && padVariant) {
      const padSize = padVariant.option1 || padVariant.title || '—';
      this.padSizeEl.textContent = padSize;
    }
    
    // Update sticky button text and image
    if (rugVariant) {
      const rugSize = rugVariant.option1 || rugVariant.title || '—';
      
      if (this.stickyText) {
        this.stickyText.textContent = rugSize;
        console.log('✓ Updated sticky text:', rugSize);
      }
      
      // Update sticky image if variant has image
      if (this.stickyBar) {
        const stickyImage = this.stickyBar.querySelector('[data-sticky-image] img');
        
        // Try variant image first, then featured image
        let imageUrl = null;
        if (rugVariant.featured_image?.src) {
          imageUrl = rugVariant.featured_image.src;
        } else if (rugVariant.image_id) {
          // Find image by ID from product data
          const productImages = this.rugData.images || [];
          const variantImage = productImages.find(img => img.id === rugVariant.image_id);
          if (variantImage) imageUrl = variantImage.src;
        }
        
        if (stickyImage && imageUrl) {
          stickyImage.src = imageUrl;
          console.log('✓ Updated sticky image');
        }
      }
    }
  }

  /**
   * Enable the add to cart button
   */
  enableButton() {
    if (this.button) {
      this.button.disabled = false;
      this.button.classList.remove('disabled');
      console.log('✓ Main button enabled');
    }
    
    // Enable sticky button too
    if (this.stickyButton) {
      this.stickyButton.disabled = false;
      this.stickyButton.classList.remove('disabled');
      console.log('✓ Sticky button enabled - Should show on scroll now!');
      
      // Trigger scroll check to update sticky visibility
      window.dispatchEvent(new Event('scroll'));
    } else {
      console.warn('⚠️ Sticky button not found - cannot enable');
    }
    
    this.hideMessage();
  }

  /**
   * Disable the add to cart button with reason
   */
  disableButton(reason = '') {
    if (this.button) {
      this.button.disabled = true;
      this.button.classList.add('disabled');
    }
    
    // Disable sticky button too
    if (this.stickyButton) {
      this.stickyButton.disabled = true;
      console.log('✓ Sticky button disabled:', reason);
    }
    
    if (reason) {
      this.showMessage(reason, 'warning');
    }
  }

  /**
   * Toggle tooltip visibility
   */
  toggleTooltip() {
    if (!this.tooltipContent) return;
    
    const isHidden = this.tooltipContent.hidden;
    if (isHidden) {
      this.tooltipContent.hidden = false;
      this.tooltipTrigger.setAttribute('aria-expanded', 'true');
    } else {
      this.closeTooltip();
    }
  }

  closeTooltip() {
    if (this.tooltipContent) {
      this.tooltipContent.hidden = true;
      this.tooltipTrigger.setAttribute('aria-expanded', 'false');
    }
  }

  /**
   * Show a message to the user
   */
  showMessage(text, type = 'info') {
    if (!this.messageEl) return;
    
    this.messageEl.textContent = text;
    this.messageEl.className = `free-cushion-pad-offer__message free-cushion-pad-offer__message--${type}`;
    this.messageEl.hidden = false;
  }

  hideMessage() {
    if (this.messageEl) {
      this.messageEl.hidden = true;
    }
  }

  /**
   * Handle add to cart action
   */
  async handleAddToCart() {
    if (this.isLoading || !this.currentRugVariantId || !this.currentPadVariantId) return;
    
    this.isLoading = true;
    this.setButtonLoading(true);
    this.hideMessage();
    
    try {
      // Add both items to cart
      const items = [
        { id: this.currentRugVariantId, quantity: 1 },
        { id: this.currentPadVariantId, quantity: 1 }
      ];
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items })
      });
      
      if (!response.ok) {
        throw new Error('Failed to add items to cart');
      }
      
      const data = await response.json();
      
      // Trigger GA4 and ads tracking
      this.triggerAnalytics(data);
      
      // Success! Trigger cart refresh
      this.showMessage('✓ Added to cart! Free cushion pad included.', 'success');
      
      // Trigger Shopify cart update events (for theme cart drawer/notification)
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      
      // Dawn theme specific
      if (window.Shopify && window.Shopify.theme) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true
        }));
      }
      
      // Try to open cart drawer if it exists
      setTimeout(() => {
        const cartDrawerToggle = document.querySelector('[data-cart-drawer-toggle], .cart-drawer__toggle, #cart-icon-bubble');
        if (cartDrawerToggle) {
          cartDrawerToggle.click();
        }
      }, 300);
      
    } catch (error) {
      console.error('Add to cart error:', error);
      this.showMessage('Unable to add to cart. Please try again.', 'error');
    } finally {
      this.isLoading = false;
      this.setButtonLoading(false);
    }
  }

  /**
   * Trigger analytics events for GA4 and ads tracking
   * Maintains same tracking as original button
   */
  triggerAnalytics(cartData) {
    try {
      // Google Analytics 4 - add_to_cart event
      if (window.gtag) {
        const rugVariant = this.rugData.variants.find(v => String(v.id) === String(this.currentRugVariantId));
        const padVariant = this.cushionData.variants.find(v => String(v.id) === String(this.currentPadVariantId));
        
        // Track rug added to cart
        if (rugVariant) {
          window.gtag('event', 'add_to_cart', {
            currency: window.Shopify?.currency?.active || 'USD',
            value: (rugVariant.price || 0) / 100,
            items: [{
              item_id: this.currentRugVariantId,
              item_name: this.rugData.product_id,
              quantity: 1,
              price: (rugVariant.price || 0) / 100
            }]
          });
        }
      }

      // Facebook Pixel - AddToCart event
      if (window.fbq) {
        const rugVariant = this.rugData.variants.find(v => String(v.id) === String(this.currentRugVariantId));
        if (rugVariant) {
          window.fbq('track', 'AddToCart', {
            content_ids: [this.currentRugVariantId],
            content_type: 'product',
            value: (rugVariant.price || 0) / 100,
            currency: window.Shopify?.currency?.active || 'USD'
          });
        }
      }

      // TikTok Pixel - AddToCart event
      if (window.ttq) {
        window.ttq.track('AddToCart');
      }

      // Snapchat Pixel
      if (window.snaptr) {
        window.snaptr('track', 'ADD_CART');
      }

      // Pinterest Tag
      if (window.pintrk) {
        window.pintrk('track', 'addtocart');
      }

      // Shopify analytics
      if (window.ShopifyAnalytics) {
        window.ShopifyAnalytics.lib.track('Added Product', {
          variantId: this.currentRugVariantId
        });
      }

    } catch (error) {
      console.warn('Analytics tracking error:', error);
      // Don't let analytics errors break the cart functionality
    }
  }

  /**
   * Set loading state on button
   */
  setButtonLoading(loading) {
    // Main button
    if (this.button) {
      const loader = this.button.querySelector('.free-cushion-pad-offer__cta-loader');
      const text = this.button.querySelector('.free-cushion-pad-offer__cta-text');
      
      if (loading) {
        this.button.classList.add('loading');
        if (loader) loader.hidden = false;
        if (text) text.style.opacity = '0.5';
      } else {
        this.button.classList.remove('loading');
        if (loader) loader.hidden = true;
        if (text) text.style.opacity = '1';
      }
    }
    
    // Sticky button
    if (this.stickyButton) {
      const loader = this.stickyButton.querySelector('.free-cushion-pad-offer__sticky-loader');
      const text = this.stickyButton.querySelector('[data-sticky-button-text]');
      
      if (loading) {
        this.stickyButton.classList.add('loading');
        if (loader) loader.hidden = false;
        if (text) text.style.opacity = '0.5';
      } else {
        this.stickyButton.classList.remove('loading');
        if (loader) loader.hidden = true;
        if (text) text.style.opacity = '1';
      }
    }
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFreeCushionPadOffer);
} else {
  initFreeCushionPadOffer();
}

function initFreeCushionPadOffer() {
  const containers = document.querySelectorAll('.free-cushion-pad-offer');
  containers.forEach(container => {
    new FreeCushionPadOffer(container);
  });
}

// Also initialize on Shopify section load (for theme editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', function(event) {
    const container = event.target.querySelector('.free-cushion-pad-offer');
    if (container) {
      new FreeCushionPadOffer(container);
    }
  });
}

